import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ExtensionOffOutlinedIcon from '@mui/icons-material/ExtensionOffOutlined';
import { useAuthStore } from '@/auth/authStore';
import { Perm } from '@/auth/permissions';
import { useModuleStore } from './moduleStore';

/** What a person sees at a module's URL when their organisation does not have the module on. */
export function ModuleDisabledPage({ module }: { module: string }) {
  const navigate = useNavigate();
  const name = useModuleStore((s) => s.names[module]) ?? module;
  const canManage = useAuthStore((s) => s.hasPermission)(Perm.Settings.Manage);
  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>{name}</Typography>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', py: 6 }}>
          <ExtensionOffOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Module not enabled</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 460, mx: 'auto' }}>
            {canManage
              ? `The ${name} module is not switched on for your organisation. You can enable it under Administration › Modules once the platform operator has subscribed your organisation to it.`
              : `The ${name} module is not switched on for your organisation. Ask your administrator if you need it.`}
          </Typography>
          {canManage && (
            <Button variant="outlined" sx={{ mt: 3, alignSelf: 'center' }} onClick={() => navigate('/administration?tab=modules')}>
              Manage modules
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * Renders its children only when the organisation has the module on; otherwise the
 * "module not enabled" card, in place, with no redirect — the URL stays what the person typed.
 * Nothing is rendered until the store has an answer, so a reload never flashes the wrong page.
 * Compose it inside `RequirePermission`: permission first, then the module.
 */
export default function RequireModule({ module, children }: { module: string; children: ReactNode }) {
  const loaded = useModuleStore((s) => s.loaded);
  const enabled = useModuleStore((s) => s.enabled[module] === true);
  if (!loaded) return null;
  if (!enabled) return <ModuleDisabledPage module={module} />;
  return <>{children}</>;
}
